// Shared AudioContext for browser autoplay compliance.
// Call unlockAudio() from a direct user gesture (e.g., button click) before attempting to play tones.
import { Capacitor } from "@capacitor/core";
import { createLogger } from "@/lib/logger";
const log = createLogger("audio");


let sharedAudioContext: AudioContext | null = null;
let unlockAttempted = false;

/**
 * iOS native SIP path (ElefaSip) owns AVAudioSession through CallKit +
 * libwebrtc. If WKWebView creates a WebAudio AudioContext for any reason
 * (ringtones, pre-warm, call-connected chimes, etc.), it grabs the audio
 * session in `playback` category and silently fights CallKit - producing
 * `AudioSession::beginInterruption but session is already interrupted!`
 * spam and one-way audio.
 *
 * Per mem://telephony/ios-no-webview-audio-prime-on-native-path, the
 * WebAudio path MUST be a no-op on iOS native. Web/PWA and Android are
 * unaffected. Android uses SIP.js inside the WebView with its own
 * AudioFocusCoordinator on the native side.
 */
function isIosNativeSipPath(): boolean {
  try {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
  } catch {
    return false;
  }
}

/**
 * Returns the shared AudioContext, creating it if needed.
 * Use this from hooks that need audio (e.g., useRingtone).
 *
 * Returns `null` on iOS native - see `isIosNativeSipPath` above.
 */
export function getSharedAudioContext(): AudioContext | null {
  if (isIosNativeSipPath()) return null;

  if (sharedAudioContext && sharedAudioContext.state !== 'closed') {
    return sharedAudioContext;
  }

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const AudioContextCtor: any = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextCtor) return null;

    sharedAudioContext = new AudioContextCtor();
    log.debug('[Audio] Created shared AudioContext, state:', sharedAudioContext.state);
    return sharedAudioContext;
  } catch (err) {
    log.error('[Audio] Failed to create AudioContext:', err);
    return null;
  }
}

/**
 * Unlocks the shared AudioContext for playback.
 * MUST be called from a direct user gesture (click/tap) to satisfy browser autoplay policies.
 * Call this in your handleCall onClick before any async work.
 */
export async function unlockAudio(): Promise<boolean> {
  try {
    const ctx = getSharedAudioContext();
    if (!ctx) {
      log.warn('[Audio] No AudioContext available');
      return false;
    }

    unlockAttempted = true;

    // Resume if suspended
    if (ctx.state === 'suspended') {
      log.debug('[Audio] Resuming suspended AudioContext...');
      await ctx.resume();
      log.debug('[Audio] AudioContext resumed, state:', ctx.state);
    }

    // Play a tiny silent blip to fully unlock on iOS/Safari
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    gain.gain.value = 0.0001; // Nearly silent

    osc.connect(gain);
    gain.connect(ctx.destination);

    const now = ctx.currentTime;
    osc.start(now);
    osc.stop(now + 0.02);

    log.debug('[Audio] Unlock complete, state:', ctx.state);
    return ctx.state === 'running';
  } catch (err) {
    log.error('[Audio] Unlock failed:', err);
    return false;
  }
}

/**
 * Returns true if we've attempted to unlock audio (doesn't guarantee success).
 */
export function isAudioUnlockAttempted(): boolean {
  return unlockAttempted;
}

/**
 * Tries to resume the shared AudioContext if it exists and is suspended.
 * Can be called from anywhere, but works best after unlockAudio() was called from a gesture.
 */
export async function resumeAudioContext(): Promise<boolean> {
  const ctx = sharedAudioContext;
  if (!ctx) return false;

  if (ctx.state === 'suspended') {
    try {
      await ctx.resume();
      log.debug('[Audio] Context resumed, state:', ctx.state);
    } catch (err) {
      log.error('[Audio] Resume failed:', err);
      return false;
    }
  }

  return ctx.state === 'running';
}

// =============================================================================
// iOS Safari audio element unlock
// =============================================================================

// Minimal valid WAV - plays silently to "prime" <audio> element playback on iOS
const SILENT_WAV = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA';

/**
 * Unlocks <audio> element playback on iOS Safari.
 * MUST be called synchronously within a user gesture (click/tap) BEFORE any async work.
 * This is separate from unlockAudio() which only resumes the AudioContext.
 */
export function unlockAudioPlayback(): void {
  if (isIosNativeSipPath()) return;

  try {
    const audio = new Audio(SILENT_WAV);
    audio.volume = 0.01;
    audio.play().then(() => {
      setTimeout(() => { audio.pause(); audio.remove(); }, 100);
    }).catch(() => {});
    // Reuse shared AudioContext instead of creating orphan instances
    const ctx = getSharedAudioContext();
    if (ctx && ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
  } catch { /* ignore */ }
}

// =============================================================================
// DTMF Tones - Standard dual-tone multi-frequency signaling
// =============================================================================

// DTMF frequencies (ITU-T Q.23)
// Each key produces a combination of a low and high frequency
const DTMF_FREQUENCIES: Record<string, [number, number]> = {
  '1': [697, 1209],
  '2': [697, 1336],
  '3': [697, 1477],
  'A': [697, 1633],
  '4': [770, 1209],
  '5': [770, 1336],
  '6': [770, 1477],
  'B': [770, 1633],
  '7': [852, 1209],
  '8': [852, 1336],
  '9': [852, 1477],
  'C': [852, 1633],
  '*': [941, 1209],
  '0': [941, 1336],
  '#': [941, 1477],
  'D': [941, 1633],
};

// Default DTMF tone duration in milliseconds
const DEFAULT_DTMF_DURATION_MS = 100;
const DTMF_VOLUME = 0.15; // Comfortable volume level

// Track active oscillators to prevent overlapping
let dtmfOscillators: OscillatorNode[] = [];
let dtmfGain: GainNode | null = null;

/**
 * Play a DTMF tone for a given digit/key
 * @param key The key pressed (0-9, *, #, A-D)
 * @param durationMs Duration in milliseconds (default: 100ms)
 */
export function playDTMFTone(key: string, durationMs: number = DEFAULT_DTMF_DURATION_MS): void {
  const frequencies = DTMF_FREQUENCIES[key.toUpperCase()];
  if (!frequencies) {
    log.warn(`[DTMF] Unknown key: ${key}`);
    return;
  }

  // iOS native: getSharedAudioContext() returns null to protect CallKit's
  // AVAudioSession from being grabbed by a long-lived WebAudio context
  // (see isIosNativeSipPath). Pre-call keypad tones, however, must still
  // be audible. Spin up a short-lived AudioContext per tone and close it
  // immediately so AVAudioSession is released before the next user action.
  let ctx = getSharedAudioContext();
  let ephemeralCtx: AudioContext | null = null;
  if (!ctx) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const AudioContextCtor: any = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextCtor) {
        log.warn('[DTMF] No AudioContext available');
        return;
      }
      ephemeralCtx = new AudioContextCtor();
      ctx = ephemeralCtx;
      if (ctx && ctx.state === 'suspended') {
        ctx.resume().catch(() => { /* fire-and-forget */ });
      }
    } catch (err) {
      log.error('[DTMF] Failed to create ephemeral AudioContext:', err);
      return;
    }
  }
  if (!ctx) return;

  // Stop any currently playing DTMF tones (only safe on the shared ctx).
  if (!ephemeralCtx) stopDTMFTone();

  try {
    // Build a per-tone gain node. We never reuse a gain node tied to an
    // ephemeral context - it would be closed before the next press.
    const gain = ephemeralCtx ? ctx.createGain() : (dtmfGain && dtmfGain.context === ctx ? dtmfGain : ctx.createGain());
    if (!ephemeralCtx) {
      if (dtmfGain !== gain) {
        try { dtmfGain?.disconnect(); } catch { /* ignore */ }
        dtmfGain = gain;
        gain.connect(ctx.destination);
      }
    } else {
      gain.connect(ctx.destination);
    }

    // Set volume with a quick fade-in to prevent clicks
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(DTMF_VOLUME, ctx.currentTime + 0.005);

    // Create two oscillators for the dual-tone
    const [lowFreq, highFreq] = frequencies;
    const oscillators: OscillatorNode[] = [];

    [lowFreq, highFreq].forEach(freq => {
      const osc = ctx!.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      osc.connect(gain);
      oscillators.push(osc);
    });

    if (!ephemeralCtx) dtmfOscillators = oscillators;

    const now = ctx.currentTime;
    const stopTime = now + durationMs / 1000;

    gain.gain.setValueAtTime(DTMF_VOLUME, stopTime - 0.01);
    gain.gain.linearRampToValueAtTime(0, stopTime);

    oscillators.forEach(osc => {
      osc.start(now);
      osc.stop(stopTime);
    });

    // Clean up after tone ends. For ephemeral contexts, close the context
    // so iOS releases AVAudioSession before CallKit might need it.
    setTimeout(() => {
      oscillators.forEach(osc => {
        try { osc.disconnect(); } catch { /* ignore */ }
      });
      if (!ephemeralCtx && dtmfOscillators === oscillators) {
        dtmfOscillators = [];
      }
      if (ephemeralCtx) {
        try { gain.disconnect(); } catch { /* ignore */ }
        ephemeralCtx.close().catch(() => { /* ignore */ });
      }
    }, durationMs + 80);

  } catch (err) {
    log.error('[DTMF] Failed to play tone:', err);
    if (ephemeralCtx) {
      try { ephemeralCtx.close(); } catch { /* ignore */ }
    }
  }
}

/**
 * Stop any currently playing DTMF tone
 */
export function stopDTMFTone(): void {
  dtmfOscillators.forEach(osc => {
    try { 
      osc.stop(); 
      osc.disconnect(); 
    } catch { /* ignore */ }
  });
  dtmfOscillators = [];
}

/**
 * Play a sequence of DTMF tones (e.g., for auto-dial)
 * @param digits String of digits to play
 * @param toneDurationMs Duration of each tone
 * @param gapMs Gap between tones
 */
export async function playDTMFSequence(
  digits: string, 
  toneDurationMs: number = 100, 
  gapMs: number = 50
): Promise<void> {
  for (const digit of digits) {
    if (DTMF_FREQUENCIES[digit.toUpperCase()]) {
      playDTMFTone(digit, toneDurationMs);
      await new Promise(resolve => setTimeout(resolve, toneDurationMs + gapMs));
    }
  }
}

// =============================================================================
// Cross-provider volume control
// =============================================================================

/**
 * Set the volume on whichever remote audio element exists (Twilio or FreeSWITCH).
 * @param volume 0.0 to 1.0
 */
export function setCallVolume(volume: number): void {
  const clampedVolume = Math.max(0, Math.min(1, volume));
  ['twilio-remote-audio', 'freeswitch-remote-audio'].forEach(id => {
    const el = document.getElementById(id) as HTMLAudioElement | null;
    if (el) {
      el.volume = clampedVolume;
    }
  });
}

// =============================================================================
// Ringtone audio element priming (iOS Safari)
// =============================================================================

let primedRingtoneElement: HTMLAudioElement | null = null;

/**
 * Pre-creates and unlocks an HTMLAudioElement for ringtone playback.
 * MUST be called synchronously within a user gesture (tap/click).
 * The primed element is reused by useRingtone to bypass iOS autoplay restrictions.
 */
export function primeRingtoneAudio(): HTMLAudioElement {
  if (isIosNativeSipPath()) {
    return new Audio();
  }
  if (primedRingtoneElement) return primedRingtoneElement;
  const audio = new Audio(SILENT_WAV);
  audio.loop = true;
  audio.volume = 1.0;
  (audio as any).playsInline = true;
  audio.setAttribute('playsinline', 'true');
  // Play + pause to unlock the element within the gesture
  audio.play().then(() => audio.pause()).catch(() => {});
  primedRingtoneElement = audio;
  log.debug('[Audio] Primed ringtone audio element');
  return audio;
}

/**
 * Returns the pre-primed audio element if available.
 * Used by useRingtone to reuse the gesture-unlocked element.
 */
export function getPrimedAudioElement(): HTMLAudioElement | null {
  return primedRingtoneElement;
}

// =============================================================================
// Remote audio element priming (iOS Safari)
// =============================================================================

/**
 * Pre-creates (or reuses) an HTMLAudioElement by ID and primes it for iOS Safari.
 * MUST be called synchronously within a user gesture (tap/click).
 * This ensures the element is authorized for .play() when srcObject is later assigned.
 */
export function primeRemoteAudioElement(id: string): HTMLAudioElement {
  if (isIosNativeSipPath()) {
    return document.getElementById(id) as HTMLAudioElement | null ?? document.createElement('audio');
  }
  let el = document.getElementById(id) as HTMLAudioElement | null;
  if (!el) {
    el = document.createElement('audio');
    el.id = id;
    document.body.appendChild(el);
  }
  el.autoplay = true;
  el.setAttribute('playsinline', 'true');
  (el as any).playsInline = true;
  el.volume = 1.0;

  // Prime with silent WAV within the gesture context.
  // CRITICAL: Do NOT pause or clear src after - iOS revokes playback authorization if paused.
  el.src = SILENT_WAV;
  el.play().then(() => {
    log.debug(`[Audio] Primed remote audio element #${id}, readyState=${el!.readyState}`);
  }).catch((err) => {
    log.warn(`[Audio] Prime play() rejected for #${id}:`, err);
  });

  return el;
}

// =============================================================================
// Call progress tones
// =============================================================================

let progressToneOscillators: OscillatorNode[] = [];
let progressToneGain: GainNode | null = null;
let progressToneTimeout: ReturnType<typeof setTimeout> | null = null;

/**
 * Play a standard ringback tone (440+480 Hz, 2s on / 4s off cadence)
 * Replaces the old continuous dial tone which was incorrect for ringing state.
 */
export function playDialTone(): void {
  stopProgressTone();
  
  const ctx = getSharedAudioContext();
  if (!ctx) return;

  try {
    if (!progressToneGain || progressToneGain.context !== ctx) {
      progressToneGain = ctx.createGain();
      progressToneGain.connect(ctx.destination);
    }

    const playRingBurst = () => {
      if (!ctx || ctx.state === 'closed') return;
      
      progressToneGain!.gain.setValueAtTime(0, ctx.currentTime);
      progressToneGain!.gain.linearRampToValueAtTime(0.08, ctx.currentTime + 0.02);

      const oscillators: OscillatorNode[] = [];
      [440, 480].forEach(freq => {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = freq;
        osc.connect(progressToneGain!);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 2);
        oscillators.push(osc);
      });

      // Fade out at end of burst
      progressToneGain!.gain.setValueAtTime(0.08, ctx.currentTime + 1.95);
      progressToneGain!.gain.linearRampToValueAtTime(0, ctx.currentTime + 2);

      progressToneOscillators = oscillators;

      // Repeat: 2s tone + 4s silence = 6s cycle
      progressToneTimeout = setTimeout(playRingBurst, 6000);
    };

    playRingBurst();
  } catch (err) {
    log.error('[Audio] Failed to play ringback tone:', err);
  }
}

/**
 * Play a busy tone (indicates line is busy)
 */
export function playBusyTone(): void {
  stopProgressTone();
  
  const ctx = getSharedAudioContext();
  if (!ctx) return;

  const playBurst = () => {
    if (!ctx || !progressToneGain) return;

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = 480;
    osc.connect(progressToneGain);
    
    const osc2 = ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.value = 620;
    osc2.connect(progressToneGain);

    const now = ctx.currentTime;
    osc.start(now);
    osc.stop(now + 0.5);
    osc2.start(now);
    osc2.stop(now + 0.5);

    progressToneOscillators = [osc, osc2];
    
    // Repeat every second (500ms on, 500ms off)
    progressToneTimeout = setTimeout(playBurst, 1000);
  };

  try {
    if (!progressToneGain || progressToneGain.context !== ctx) {
      progressToneGain = ctx.createGain();
      progressToneGain.connect(ctx.destination);
    }
    progressToneGain.gain.value = 0.1;
    
    playBurst();
  } catch (err) {
    log.error('[Audio] Failed to play busy tone:', err);
  }
}

/**
 * Stop any currently playing progress tone with smooth ramp-down to prevent clicks
 */
export function stopProgressTone(): void {
  if (progressToneTimeout) {
    clearTimeout(progressToneTimeout);
    progressToneTimeout = null;
  }
  
  // Smoothly ramp down gain to prevent clicks/pops
  const ctx = getSharedAudioContext();
  if (progressToneGain && ctx && progressToneOscillators.length > 0) {
    const now = ctx.currentTime;
    try {
      progressToneGain.gain.setValueAtTime(progressToneGain.gain.value, now);
      progressToneGain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
    } catch { /* ignore */ }
    
    // Stop oscillators after ramp completes
    const oscsToStop = [...progressToneOscillators];
    progressToneOscillators = [];
    
    setTimeout(() => {
      oscsToStop.forEach(osc => {
        try { 
          osc.stop(); 
          osc.disconnect(); 
        } catch { /* ignore */ }
      });
    }, 60);
  } else {
    // Fallback: stop immediately
    progressToneOscillators.forEach(osc => {
      try { 
        osc.stop(); 
        osc.disconnect(); 
      } catch { /* ignore */ }
    });
    progressToneOscillators = [];
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// Call Event Sound Effects - futuristic, premium tones
// ═══════════════════════════════════════════════════════════════════════════

/**
 * "Connected" chime - ascending crystalline sweep (C6→E6→G6)
 * Plays when a call is answered/connected. Short, satisfying, futuristic.
 */
export function playCallConnectedSound(): void {
  try {
    const ctx = getSharedAudioContext();
    if (!ctx || ctx.state !== 'running') return;

    const now = ctx.currentTime;
    const masterGain = ctx.createGain();
    masterGain.gain.setValueAtTime(0.18, now);
    masterGain.gain.linearRampToValueAtTime(0, now + 0.55);
    masterGain.connect(ctx.destination);

    // Three-note ascending arpeggio: C6 → E6 → G6
    const notes = [1047, 1319, 1568]; // Hz
    const spacing = 0.08; // 80ms between notes

    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const noteGain = ctx.createGain();
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + i * spacing);
      
      noteGain.gain.setValueAtTime(0, now + i * spacing);
      noteGain.gain.linearRampToValueAtTime(1, now + i * spacing + 0.015);
      noteGain.gain.exponentialRampToValueAtTime(0.001, now + i * spacing + 0.4);
      
      osc.connect(noteGain);
      noteGain.connect(masterGain);
      
      osc.start(now + i * spacing);
      osc.stop(now + i * spacing + 0.45);
    });

    // Subtle shimmer overtone
    const shimmer = ctx.createOscillator();
    const shimmerGain = ctx.createGain();
    shimmer.type = 'sine';
    shimmer.frequency.setValueAtTime(3136, now); // G7
    shimmerGain.gain.setValueAtTime(0.03, now);
    shimmerGain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
    shimmer.connect(shimmerGain);
    shimmerGain.connect(masterGain);
    shimmer.start(now + 0.15);
    shimmer.stop(now + 0.55);

    log.debug('[Audio] Call connected sound played');
  } catch (err) {
    log.warn('[Audio] Failed to play connected sound:', err);
  }
}

/**
 * "Disconnected" tone - descending two-note drop (G5→C5)
 * Plays when a call ends. Clean, definitive, not jarring.
 */
export function playCallEndedSound(): void {
  try {
    const ctx = getSharedAudioContext();
    if (!ctx || ctx.state !== 'running') return;

    const now = ctx.currentTime;
    const masterGain = ctx.createGain();
    masterGain.gain.setValueAtTime(0.15, now);
    masterGain.gain.linearRampToValueAtTime(0, now + 0.45);
    masterGain.connect(ctx.destination);

    // Two-note descending: G5 → C5
    const notes = [784, 523]; // Hz
    const spacing = 0.1;

    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const noteGain = ctx.createGain();
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + i * spacing);
      
      noteGain.gain.setValueAtTime(0, now + i * spacing);
      noteGain.gain.linearRampToValueAtTime(1, now + i * spacing + 0.01);
      noteGain.gain.exponentialRampToValueAtTime(0.001, now + i * spacing + 0.3);
      
      osc.connect(noteGain);
      noteGain.connect(masterGain);
      
      osc.start(now + i * spacing);
      osc.stop(now + i * spacing + 0.35);
    });

    log.debug('[Audio] Call ended sound played');
  } catch (err) {
    log.warn('[Audio] Failed to play ended sound:', err);
  }
}

/**
 * Low-balance warning - three short beeps before automatic call cutoff.
 * Web-only; iOS native returns no AudioContext so CallKit/libwebrtc audio is not disturbed.
 */
export function playLowBalanceWarningSound(): void {
  try {
    const ctx = getSharedAudioContext();
    if (!ctx || ctx.state !== 'running') return;

    const now = ctx.currentTime;
    const masterGain = ctx.createGain();
    masterGain.gain.setValueAtTime(0.16, now);
    masterGain.connect(ctx.destination);

    [0, 0.45, 0.9].forEach((offset) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(740, now + offset);
      gain.gain.setValueAtTime(0, now + offset);
      gain.gain.linearRampToValueAtTime(1, now + offset + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + offset + 0.22);
      osc.connect(gain);
      gain.connect(masterGain);
      osc.start(now + offset);
      osc.stop(now + offset + 0.24);
    });

    setTimeout(() => masterGain.disconnect(), 1500);
  } catch (err) {
    log.warn('[Audio] Failed to play low-balance warning:', err);
  }
}
